import { LANGUAGE_PRESETS } from '../../config/template_labels';
import { classifyIntent, findSimilarExamples, RAG_EXAMPLES } from './intentRouter';
import { canUseFeature } from '../../config/pricing';

export function isLiquidTemplate(template) {
    return (template || '').indexOf('{%') !== -1;
}

const COLOR_PALETTES = [
    { name: "Slate Blue", colors: ['#334155', '#f8fafc', '#e2e8f0', '#1e3a5f', '#4a6fa5'] },
    { name: "Charcoal", colors: ['#264653', '#2a9d8f', '#e9c46a', '#FAF1DD', '#e76f51'] },
    { name: "Imperial Red", colors: ['#1d3557', '#457b9d', '#a8dadc', '#f1faee', '#e63946'] },
    { name: "Navy Blue", colors: ['#03045e', '#0077b6', '#48cae4', '#ade8f4', '#023e8a'] },
    { name: "Teal", colors: ['#0f172a', '#f8fafc', '#e2e8f0', '#0d9488', '#115e59'] },
    { name: "Forest", colors: ['#1c1917', '#fafaf9', '#e7e5e4', '#1c3829', '#4d7c5e'] },
    { name: "Burnt Orange", colors: ['#18181b', '#fafafa', '#e4e4e7', '#c2410c', '#ea580c'] },
    { name: "Deep Red", colors: ['#1c1917', '#fafaf9', '#d6d3d1', '#7f1d1d', '#991b1b'] },
    { name: "Stone", colors: ['#292524', '#fafaf9', '#d6d3d1', '#44403c', '#78716c'] },
    { name: "Medical Teal", colors: ['#1f2937', '#f9fafb', '#e5e7eb', '#0f4c5c', '#1b7a8a'] },
    { name: "Luxury Gold", colors: ['#1c1917', '#fafaf9', '#e7e5e4', '#1c1917', '#92754c'] },
    { name: "Apricot", colors: ['#6d6875', '#b5838d', '#e5989b', '#FCEBE9', '#ffcdb2'] },
    { name: "Paradise", colors: ['#073b4c', '#118ab2', '#06d6a0', '#ffd166', '#ef476f'] },
    { name: "Eggshell", colors: ['#3d405b', '#81b29a', '#f2cc8f', '#f4f1de', '#e07a5f'] },
    { name: "Jet", colors: ['#284b63', '#d9d9d9', '#ffffff', '#3c6e71', '#353535'] },
    { name: "Independence", colors: ['#555b6e', '#89b0ae', '#faf9f9', '#bee3db', '#ffd6ba'] },
    { name: "Rich Black", colors: ['#001524', '#15616d', '#ffecd1', '#FCFCFC', '#78290f'] },
];

export { COLOR_PALETTES };

export function buildLLMSystemPrompt(input, semanticMatches) {
    var examples = findSimilarExamples(input);

    if (semanticMatches && semanticMatches.length > 0) {
        var semanticExamples = semanticMatches
            .filter(function(m) { return m.score > 0.4 && m.index < RAG_EXAMPLES.length; })
            .map(function(m) { return RAG_EXAMPLES[m.index]; });
        var seen = new Set(examples.map(function(e) { return e.query; }));
        semanticExamples.forEach(function(se) {
            if (!seen.has(se.query)) { examples.push({ query: se.query, edits: se.edits, score: 0 }); seen.add(se.query); }
        });
        examples = examples.slice(0, 5);
    }

    var examplesText = '';
    if (examples.length > 0) {
        examplesText = '\n\nEXAMPLES of correct edits:\n' + examples.map(function(ex) {
            return 'Q: "' + ex.query + '"\nA: ' + JSON.stringify({ explanation: ex.query, edits: ex.edits });
        }).join('\n');
    }

    var snippetRef = '\n\nTEMPLATE ENGINE:\n' +
        'Templates use Mustache/Liquid syntax. Both {{variable}} and {% if var %}...{% endif %} work.\n' +
        'Conditionals: {{#var}}shown if truthy{{/var}} / {{^var}}shown if falsy{{/var}}\n' +
        'Loops: {{#items}}{{field}}{{/items}}\n\n' +
        'AVAILABLE VARIABLES:\n' +
        'BODY (full template context):\n' +
        '- seller.company, seller.taxId, seller.registrationNumber, seller.phone, seller.website\n' +
        '- seller.address.street, seller.address.number, seller.address.zip, seller.address.city, seller.address.country\n' +
        '- seller.bank.name, seller.bank.iban, seller.bank.swift, seller.bank.currency\n' +
        '- seller.logo (URL), seller.colorPalette.color1..color5, seller.colorPalette.name\n' +
        '- order.order_number, order.name, order.created_at, order.note, order.currency\n' +
        '- order.billing_address.name, .address1, .city, .zip, .country, .province, .phone, .company\n' +
        '- order.shipping_address.name, .address1, .city, .zip, .country, .province, .phone\n' +
        '- order.customer.first_name, .last_name, .email, .phone\n' +
        '- order.fulfillments[].tracking_number, .tracking_company, .tracking_url\n' +
        '- order.payment_gateway_names[], order.financial_status, order.fulfillment_status\n' +
        '- order.tags (string), customer_tags.TAG_NAME (boolean per tag)\n' +
        '- order.meta.NAMESPACE.FIELD_NAME (order-level metafield, e.g. order.meta.custom.po_ref)\n' +
        '- invoice.number.id, invoice.number.series (prefix), invoice.date, invoice.dueDate\n' +
        '- invoice.final.price, invoice.final.discount, invoice.currency.main, invoice.currency.secondary\n' +
        '- invoice.taxes.vta (total tax), invoice.taxes.show (boolean)\n' +
        '- invoice.isPaid (boolean), invoice.url (public download link)\n' +
        '- total.main, total.secondary (subtotals)\n' +
        '- description_rows (rendered row HTML — insert this in the table body)\n' +
        '- settings.showShipping, settings.showBankDetails, settings.showPaidBadge, settings.showNotes\n' +
        '- settings.showTracking, settings.showTerms, settings.showSignatureLines, settings.showTaxColumns\n' +
        '- settings.showQrCode, settings.showSku, settings.showImages, settings.showDiscount\n' +
        '- settings.showWeight, settings.showHsCode, settings.showSubtotal, settings.showTaxBreakdown\n' +
        '- settings.showShippingCost, settings.showDueDate, settings.dateFormat, settings.paymentTerms\n' +
        '- labels.description, labels.qty, labels.unitPrice, labels.amount, labels.vat, labels.rate\n' +
        '- labels.gross, labels.grossTotal, labels.total, labels.subtotal, labels.shipping, labels.discount\n' +
        '- showVTA (boolean — controls tax column visibility in header/rows)\n' +
        '- {{#formatDate}}{{order.created_at}}, DD/MM/YYYY{{/formatDate}} — format any date\n' +
        '- {{#formatCurrency}}{{invoice.final.price}}, en-US, USD{{/formatCurrency}} — format currency\n' +
        '\nROW TEMPLATE (each line item in the table):\n' +
        '- nrCrt (row number), description, sku, quantity, unit\n' +
        '- unitPrice (net price), unitPrice.main, unitPrice.gross (price with tax)\n' +
        '- amount.main (quantity * unitPrice), amount.gross (with tax)\n' +
        '- taxPrice (tax amount), taxRate (tax %)\n' +
        '- showVTA (boolean — hide/show tax cells)\n' +
        '- item.weight, item.total_discount, item.variant_title, item.properties[]\n' +
        '- variant.hscode (HS/Harmonized code), variant.product.meta.FIELD_NAME\n' +
        '- image.src (product image URL)\n' +
        '\nSNIPPET BLOCKS (use these exact patterns):\n' +
        '- QR Code: <div id="qr-code" data-url="{{invoice.url}}"></div>\n' +
        '- EPC Girocode: <div id="epc-qr" data-iban="{{seller.bank.iban}}" data-bic="{{seller.bank.swift}}" data-recipient="{{seller.company}}" data-amount="{{invoice.final.price}}" data-currency="{{invoice.currency.main}}" data-reference="{{invoice.number.series}}{{invoice.number.id}}"></div>\n' +
        '- Barcode: <p style="font-family:\'Libre Barcode 128\';font-size:48px;text-align:center;">{{order.order_number}}</p>\n' +
        '- Barcode 39: <p style="font-family:\'Libre Barcode 39 Text\';font-size:48px;text-align:center;">*{{order.order_number}}*</p>\n' +
        '- Paid Badge: {{#invoice.isPaid}}{{#settings.showPaidBadge}}<div style="position:absolute;top:30px;right:30px;border:3px solid #22c55e;color:#22c55e;padding:6px 18px;font-size:18px;font-weight:700;letter-spacing:2px;transform:rotate(-12deg);opacity:0.8;">PAID</div>{{/settings.showPaidBadge}}{{/invoice.isPaid}}\n' +
        '- Logo: <img src="{{seller.logo}}" style="max-height:60px;" onerror="this.style.display=\'none\'" />\n' +
        '\nCONDITIONAL PATTERNS:\n' +
        '- Tax columns in header: {{#invoice.taxes.show}}<th>...</th>{{/invoice.taxes.show}}\n' +
        '- Tax cells in rows: {{#showVTA}}<td>{{taxPrice}}</td><td>{{taxRate}}%</td>{{/showVTA}}\n' +
        '- Settings gate: {{#settings.showShipping}}...{{/settings.showShipping}}\n' +
        '- Tag-based content: {{#customer_tags.wholesale}}Wholesale price{{/customer_tags.wholesale}}\n' +
        '\nRULES:\n' +
        '- Barcodes use font-family (Libre Barcode 128 or Libre Barcode 39 Text), NOT <img> tags.\n' +
        '- QR codes and EPC are rendered server-side from <div id="..."> with data attributes.\n' +
        '- NEVER add new <td> columns to the row template — it breaks header alignment. Insert inside existing <td>.\n' +
        '- To REMOVE a column: delete the <th> from the body AND the <td> from the row template.\n' +
        '- To HIDE a column conditionally: wrap both <th> and <td> in {{#showVTA}}...{{/showVTA}} or {{#settings.KEY}}.\n' +
        '- Colors: use {{seller.colorPalette.color1}} through .color5 for theme-aware styling.';

    var systemPrompt = 'You are an expert invoice template editor. You modify Mustache/Liquid HTML templates for a Shopify invoice app. Output ONLY valid JSON.\n\n' +
        'FORMAT: {"explanation":"short description of change","edits":[{"target":"body"|"row","search":"EXACT substring from template","replace":"new code"}]}\n\n' +
        'RULES:\n' +
        '- "search" MUST be a verbatim substring that exists in the provided template. Copy it exactly including whitespace.\n' +
        '- Use ONLY the variables and snippets listed in the reference. NEVER invent template variables.\n' +
        '- To ADD content: find a nearby existing element in the template and include it in "search", then in "replace" put the original + your new code.\n' +
        '- To REMOVE content: put the content to remove in "search" and use "" as "replace".\n' +
        '- Make minimal, targeted changes. Prefer one precise edit over multiple broad ones.\n' +
        '- Keep HTML valid. Never leave unclosed tags.\n' +
        '- For REMOVING columns: you MUST edit BOTH body (remove <th>) AND row (remove <td>).\n' +
        '- For conditional visibility: wrap content in {{#variable}}...{{/variable}}. Use settings.* for user-togglable features.\n' +
        '- For style/design requests: focus on inline CSS changes (colors, fonts, spacing, borders). Use colorPalette variables for theme colors.\n' +
        '- For brand-specific requests (Nike, Apple, etc.): interpret as style preferences (modern/clean/bold) and adjust CSS accordingly.\n' +
        '- Try to fulfill requests creatively within the constraints. Only return "Cannot do" if truly impossible (e.g., requires data not in variables).\n' +
        '- When removing tax/VAT columns, also remove the corresponding cells from the row template.' +
        snippetRef + examplesText;

    return systemPrompt;
}

export function buildLLMMessages(input, bodyTemplate, rowTemplate, semanticMatches) {
    var systemPrompt = buildLLMSystemPrompt(input, semanticMatches);
    var bodyExcerpt = bodyTemplate.length > 2000 ? bodyTemplate.substring(0, 2000) + '\n...(truncated)' : bodyTemplate;
    var userMsg = 'BODY TEMPLATE:\n```\n' + bodyExcerpt + '\n```\n\nROW TEMPLATE:\n```\n' + rowTemplate + '\n```\n\nREQUEST: ' + input;

    return [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMsg },
    ];
}

export function parseLLMResponse(content) {
    var proposal = null;
    try {
        var parsed = JSON.parse(content);
        if (parsed && parsed.edits) {
            proposal = parsed;
        }
    } catch (e) {
        var jsonMatch = content.match(/\{[\s\S]*"edits"[\s\S]*\}/);
        if (jsonMatch) {
            try {
                var extracted = JSON.parse(jsonMatch[0]);
                if (extracted && extracted.edits) {
                    proposal = extracted;
                }
            } catch (e2) {}
        }
        if (!proposal) {
            var fenceMatch = content.match(/```(?:json)?\s*\n?([\s\S]*?)```/);
            if (fenceMatch) {
                try {
                    var fenceParsed = JSON.parse(fenceMatch[1]);
                    if (fenceParsed && fenceParsed.edits) {
                        proposal = fenceParsed;
                    }
                } catch (e3) {}
            }
        }
        if (!proposal) {
            var bodyMatch = content.match(/```body\n([\s\S]*?)```/);
            var rowMatch = content.match(/```row\n([\s\S]*?)```/);
            if (bodyMatch || rowMatch) {
                proposal = { explanation: 'Code changes', edits: [] };
                if (bodyMatch) proposal.fullBody = bodyMatch[1].trim();
                if (rowMatch) proposal.fullRow = rowMatch[1].trim();
            }
        }
    }

    if (proposal && proposal.edits) {
        proposal.edits = proposal.edits.filter(function(e) { return e.search && e.replace !== undefined; });
    }

    if (proposal && proposal.reasoning) {
        proposal.reasoning = String(proposal.reasoning).substring(0, 200);
    }

    return proposal;
}

export function applyProposal(proposal, bodyTemplate, rowTemplate) {
    var body = bodyTemplate;
    var row = rowTemplate;
    var bodyChanged = false;
    var rowChanged = false;
    var failedEdits = [];

    if (proposal.edits && proposal.edits.length > 0) {
        proposal.edits.forEach(function(edit) {
            if (edit.target === 'body' && body.indexOf(edit.search) !== -1) {
                body = body.replace(edit.search, edit.replace);
                bodyChanged = true;
            } else if (edit.target === 'row' && row.indexOf(edit.search) !== -1) {
                row = row.replace(edit.search, edit.replace);
                rowChanged = true;
            } else if (edit.search && edit.target === 'body' && body.indexOf(edit.search) === -1) {
                body = body + '\n' + edit.replace;
                bodyChanged = true;
                failedEdits.push(edit);
            } else if (edit.search && edit.target === 'row' && row.indexOf(edit.search) === -1) {
                row = row + '\n' + edit.replace;
                rowChanged = true;
                failedEdits.push(edit);
            }
        });
    }

    if (proposal.fullBody) {
        body = proposal.fullBody;
        bodyChanged = true;
    }
    if (proposal.fullRow) {
        row = proposal.fullRow;
        rowChanged = true;
    }

    return { body, row, bodyChanged, rowChanged, failedEdits };
}

export function executeDeterministicAction(action, theme, subscription) {
    var body = theme.bodyTemplate || '';
    var row = theme.rowTemplate || '';
    var changes = [];
    var response = '';

    switch (action.intent) {
        case 'translate': {
            var preset = LANGUAGE_PRESETS.find(function(p) { return p.code === action.langCode; });
            if (preset) {
                Object.keys(preset.labels).forEach(function(key) {
                    changes.push({ path: 'labels.' + key, value: preset.labels[key] });
                });
                response = 'Translated all labels to ' + action.langName + '. Applied ' + Object.keys(preset.labels).length + ' label translations.';
            } else {
                response = 'Language "' + action.langName + '" not found in presets.';
            }
            break;
        }

        case 'toggle': {
            var newVal = action.enable;
            changes.push({ path: 'settings.' + action.setting, value: newVal });
            response = (newVal ? 'Enabled' : 'Disabled') + ' ' + action.label + '.';
            if (newVal && body.indexOf('settings.' + action.setting) === -1) {
                response += ' Note: template does not have a conditional block for this setting — it may not be visible until you add the HTML section.';
            }
            break;
        }

        case 'snippet': {
            if (action.requiredFeature && !canUseFeature(subscription, action.requiredFeature)) {
                return { changes: [], response: action.label + ' requires a Starter or Professional plan. [Upgrade](/billing) to unlock this feature.', gated: true };
            }
            var snippetCode = (action.liquidCode && isLiquidTemplate(body)) ? action.liquidCode : action.snippet;
            if (action.target === 'body') {
                changes.push({ path: 'bodyTemplate', value: body + '\n' + snippetCode });
                response = 'Inserted ' + action.label + ' snippet into body template.';
            } else {
                if (action.insertMode === 'inline') {
                    var descIdx = row.indexOf('{{description}}');
                    if (descIdx !== -1) {
                        row = row.substring(0, descIdx) + snippetCode + row.substring(descIdx);
                    } else {
                        var firstCloseTd = row.indexOf('</td>');
                        if (firstCloseTd !== -1) {
                            var secondTdStart = row.indexOf('<td', firstCloseTd);
                            var secondCloseTd = secondTdStart !== -1 ? row.indexOf('</td>', secondTdStart) : -1;
                            var insertAt = secondCloseTd !== -1 ? secondCloseTd : firstCloseTd;
                            row = row.substring(0, insertAt) + ' ' + snippetCode + row.substring(insertAt);
                        } else {
                            row = row + '\n' + snippetCode;
                        }
                    }
                } else {
                    var beforeClose = row.lastIndexOf('</tr>');
                    if (beforeClose !== -1) {
                        row = row.substring(0, beforeClose) + '    ' + snippetCode + '\n' + row.substring(beforeClose);
                    } else {
                        row = row + '\n' + snippetCode;
                    }
                }
                changes.push({ path: 'rowTemplate', value: row });
                response = 'Inserted ' + action.label + ' into row template.';
            }
            if (action.previewNote) {
                response += ' ' + action.previewNote;
            }
            break;
        }

        case 'font':
            changes.push({ path: 'settings.font', value: action.value });
            response = 'Changed font to ' + action.label + '.';
            break;

        case 'palette': {
            var colors = action.palette.colors;
            changes.push({ path: 'colorPalette.color1', value: colors[0] });
            changes.push({ path: 'colorPalette.color2', value: colors[1] });
            changes.push({ path: 'colorPalette.color3', value: colors[2] });
            changes.push({ path: 'colorPalette.color4', value: colors[3] });
            changes.push({ path: 'colorPalette.color5', value: colors[4] });
            response = 'Applied "' + action.label + '" color palette.';
            break;
        }

        case 'paper':
            changes.push({ path: 'paperSize.format', value: action.format });
            response = 'Changed paper size to ' + action.label + '.';
            break;

        case 'orientation':
            changes.push({ path: 'paperSize.orientation', value: action.value });
            response = 'Changed orientation to ' + action.value + '.';
            break;

        case 'dateFormat':
            changes.push({ path: 'settings.dateFormat', value: action.value });
            response = 'Changed date format to ' + action.value + '.';
            break;

        case 'decimals':
            changes.push({ path: 'settings.decimalPlaces', value: action.value });
            response = 'Set decimal places to ' + action.value + '.';
            break;

        case 'paymentTerms':
            changes.push({ path: 'settings.paymentTerms', value: action.value });
            response = action.value ? 'Set payment terms to ' + action.value + '.' : 'Removed payment terms.';
            break;

        case 'margin':
            changes.push({ path: 'paperSize.margin', value: action.value });
            response = 'Set margin to ' + action.value + 'cm.';
            break;

        case 'stylePreset': {
            var presetPalette = COLOR_PALETTES.find(function(p) { return p.name === action.preset.palette; });
            if (presetPalette) {
                changes.push({ path: 'colorPalette.color1', value: presetPalette.colors[0] });
                changes.push({ path: 'colorPalette.color2', value: presetPalette.colors[1] });
                changes.push({ path: 'colorPalette.color3', value: presetPalette.colors[2] });
                changes.push({ path: 'colorPalette.color4', value: presetPalette.colors[3] });
                changes.push({ path: 'colorPalette.color5', value: presetPalette.colors[4] });
            }
            changes.push({ path: 'settings.font', value: action.preset.font });
            response = action.preset.description;
            break;
        }

        case 'reposition_image': {
            var imgTag = '<img src="{{image.src}}" style="max-width:32px;max-height:32px;vertical-align:middle;margin-right:8px;" onerror="this.style.display=\'none\'" />';
            var hasImage = row.indexOf('{{image.src}}') !== -1 || row.indexOf('image.src') !== -1;
            if (hasImage) {
                var imgMatch = row.match(/<img[^>]*image\.src[^>]*\/?\s*>/);
                if (imgMatch) {
                    row = row.replace(imgMatch[0], '');
                    var descPos = row.indexOf('{{description}}');
                    if (descPos !== -1) {
                        row = row.substring(0, descPos) + imgTag + row.substring(descPos);
                    } else {
                        row = imgTag + row;
                    }
                    changes.push({ path: 'rowTemplate', value: row });
                    response = 'Moved product image to the left of the description.';
                } else {
                    response = 'Could not locate the image tag in your row template to reposition it.';
                }
            } else {
                var descPos2 = row.indexOf('{{description}}');
                if (descPos2 !== -1) {
                    row = row.substring(0, descPos2) + imgTag + row.substring(descPos2);
                } else {
                    row = imgTag + row;
                }
                changes.push({ path: 'rowTemplate', value: row });
                response = 'Added product image to the left of the description.';
            }
            break;
        }

        default:
            response = 'Action not recognized.';
    }

    return { changes, response, gated: false };
}

export function createSnapshot(theme) {
    var data = {
        bodyTemplate: theme.bodyTemplate || '',
        rowTemplate: theme.rowTemplate || '',
        labels: theme.labels || {},
        settings: theme.settings || {},
        colorPalette: theme.colorPalette || {},
        paperSize: theme.paperSize || {},
    };
    return JSON.stringify(data);
}

export function decompressSnapshot(compressed) {
    if (typeof compressed === 'object') return compressed;
    return JSON.parse(compressed);
}

export { classifyIntent, findSimilarExamples, RAG_EXAMPLES };



// WEBPACK FOOTER //
// app/copilot/CopilotEngine.js